import React from 'react';
import * as R from 'ramda';
import connectToI18n from 'modules/i18n/connectToI18n';
import PropTypes from 'prop-types';
import { Button, Grid, Heading, Text } from 'components/ui';
import { CheckboxGroup, FormSelect } from 'components/ui/form';
import { connect } from 'react-redux';
import {
    continueToInvoiceForm,
    editGiftCardForm,
    redirectToPaymentPage,
    requestInvoice,
    selectCurrency,
    selectGiftCardForm,
    selectInvoiceRequestable,
    selectInvoiceRequested,
    selectLockedForm,
    continueToLogin,
    setCurrency,
    setInvoiceRequestable,
} from 'modules/giftCard';
import { getCurrencyByCountry, getCurrencyByLangcode } from 'modules/config/currencies';
import { userSelector } from 'modules/currentUser';
import PriceInput from 'components/Forms/PriceInput.jsx';
import { formatPriceWithoutSymbol } from 'modules/utils/currency.js';
import { validate, validators } from 'modules/validation';
import { EUR, CHF } from 'modules/config/currencies.js';
import { formatPriceToAmount } from 'modules/utils/currency.js';

export class BuyGiftCardForm extends React.Component {
    static propTypes = {
        canRequestInvoice: PropTypes.bool,
        continueToInvoiceForm: PropTypes.func.isRequired,
        currency: PropTypes.string.isRequired,
        formData: PropTypes.shape({
            value: PropTypes.string.isRequired,
            amount: PropTypes.number.isRequired,
            quantity: PropTypes.number.isRequired,
        }).isRequired,
        editGiftCardForm: PropTypes.func.isRequired,
        invoiceRequested: PropTypes.bool,
        isPaymentPageBeingCreated: PropTypes.bool,
        trans: PropTypes.func.isRequired,
        i18n: PropTypes.string.isRequired,
        redirectToPaymentPage: PropTypes.func.isRequired,
        requestInvoice: PropTypes.func.isRequired,
        isAnonymous: PropTypes.bool,
        continueToLogin: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
        setCurrency: PropTypes.func.isRequired,
        setInvoiceRequestable: PropTypes.func.isRequired,
    };

    state = {
        areTermsAccepted: false,
        showErrors: false,
        clientErrors: {},
    };

    componentDidMount() {
        const { user, i18n } = this.props;

        const initialCurrency = this.getInitialCurrency({ user, i18n });
        this.props.setCurrency(initialCurrency);
        const canRequestInvoice = R.contains(initialCurrency, [EUR, CHF]);
        this.props.setInvoiceRequestable(canRequestInvoice);

        // Format the price with locale settings
        const formatedPrice = formatPriceWithoutSymbol({
            locale: i18n,
            currency: initialCurrency,
            price: initialCurrency === EUR ? '20,00' : '100,00',
        });
        this.props.editGiftCardForm('value', formatedPrice);
        this.props.editGiftCardForm('amount', formatPriceToAmount(formatedPrice));
    }

    onQuantityChange = (_name, value) =>
        this.props.editGiftCardForm('quantity', parseInt(value, 10));

    onRequestInvoiceChange = (_name, value) => this.props.requestInvoice(value);

    onSubmit = () => {
        const [isValid, clientErrors] = this.handleValidate(this.props.formData);
        if (!isValid) {
            return this.setState(
                R.evolve({
                    clientErrors: R.always(clientErrors),
                    showErrors: R.T,
                })
            );
        }
        this.setState(
            R.evolve({
                clientErrors: R.always(clientErrors),
                showErrors: R.F,
            })
        );

        if (this.props.isAnonymous) {
            return this.props.continueToLogin();
        }

        if (this.props.invoiceRequested) {
            return this.props.continueToInvoiceForm();
        }

        return this.props.redirectToPaymentPage();
    };

    onTermsAcceptedChange = () => {
        this.setState(R.evolve({ areTermsAccepted: R.not }));
    };

    getInitialCurrency = ({ user, i18n }) => {
        if (user.anonymous) {
            return getCurrencyByLangcode(i18n);
        }
        return getCurrencyByCountry(R.path(['address', 'country', 'id'], user));
    };

    handlePriceChange = (_, priceValue, price) => {
        this.props.editGiftCardForm('value', priceValue);
        this.props.editGiftCardForm('amount', price.amount);
        if (this.state.showErrors) {
            const [isValid, clientErrors] = this.handleValidate({
                amount: price.amount,
            });

            this.setState(
                R.evolve({
                    clientErrors: R.always(clientErrors),
                    showErrors: R.always(!isValid),
                })
            );
        }
    };

    handleValidate = formData => {
        const { currency } = this.props;
        const { assertValidRange } = validators(this.props.trans);

        const min = 100;
        const max = currency === EUR ? 500000 : 2000000;

        return validate.all(
            [validate.prop('amount', assertValidRange(min, max))].filter(x => x !== false)
        )(formData);
    };

    render() {
        const {
            canRequestInvoice,
            currency,
            formData,
            invoiceRequested,
            isPaymentPageBeingCreated,
            trans,
            i18n,
        } = this.props;

        const { showErrors, clientErrors } = this.state;

        const isButtonDisabled = !this.state.areTermsAccepted || formData.amount <= 0;

        return (
            <div className="buyGiftCardMainContent">
                <Heading size={2} className="u-mb-4" responsive>
                    {trans('buyGiftCard.form.title')}
                </Heading>
                <div>
                    <Grid className="u-mb-4" xs="4|8|4|8|12|12">
                        <Text className="buyGiftCardFormLabel">{trans('global.amount')}</Text>
                        <PriceInput
                            className="u-mb-4 buyGiftCardFormSelect"
                            inputClassName="buyGiftCardFormSelect"
                            name="price"
                            value={formData.value}
                            currency={currency}
                            locale={i18n}
                            onChange={this.handlePriceChange}
                            showErrors={showErrors}
                            clientErrors={clientErrors.amount}
                        />
                        <Text className="buyGiftCardFormLabel">{trans('global.quantity')}</Text>
                        <FormSelect
                            className="u-mb-4 buyGiftCardFormSelect"
                            name="quantityChoice"
                            onChange={this.onQuantityChange}
                            options={R.range(1, 11).map(x => ({
                                label: x.toString(),
                                value: x.toString(),
                            }))}
                            value={formData.quantity.toString()}
                        />
                        {canRequestInvoice && (
                            <CheckboxGroup
                                id="invoiceRequested"
                                isChecked={invoiceRequested}
                                onChange={this.onRequestInvoiceChange}
                            >
                                {trans('buyGiftCard.invoiceRequested')}
                            </CheckboxGroup>
                        )}
                        {
                            <CheckboxGroup
                                id="termsAccepted"
                                isChecked={this.state.areTermsAccepted}
                                onChange={this.onTermsAcceptedChange}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: trans('buyGiftCard.termsAccepted'),
                                    }}
                                />
                            </CheckboxGroup>
                        }
                    </Grid>
                    <Button
                        block
                        disabled={isButtonDisabled}
                        loading={isPaymentPageBeingCreated}
                        onClick={this.onSubmit}
                        variant="success"
                    >
                        {trans('global.continue')}
                    </Button>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    canRequestInvoice: selectInvoiceRequestable(state),
    currency: selectCurrency(state),
    formData: selectGiftCardForm(state),
    invoiceRequested: selectInvoiceRequested(state),
    isPaymentPageBeingCreated: selectLockedForm(state),
    user: userSelector(state),
});

export default connect(mapStateToProps, {
    continueToInvoiceForm,
    editGiftCardForm,
    redirectToPaymentPage,
    requestInvoice,
    continueToLogin,
    setCurrency,
    setInvoiceRequestable,
})(connectToI18n(BuyGiftCardForm));
